import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CalendarBlank, 
  CaretLeft, 
  CaretRight, 
  Plus, 
  X,
  MapPin,
  Clock,
  Briefcase,
  CheckSquare,
  CurrencyDollar,
  FileText,
  Bell,
  Users,
  Trash,
  PencilSimple,
  SpinnerGap,
  Warning,
  ArrowsClockwise,
  Gear,
  EnvelopeSimple,
  PaperPlaneTilt
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { calendarAPI } from '../api/api';
import { BreadcrumbsBox } from '../components/Breadcrumbs';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  addDays, 
  addMonths, 
  subMonths, 
  isSameMonth, 
  isSameDay, 
  parseISO,
  isToday 
} from 'date-fns';

const Calendar = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [userType, setUserType] = useState('');
  
  // New event form
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    all_day: true,
    event_type: 'custom',
    location: '',
    color: '#2B4C6F'
  });
  
  // Sync state
  const [syncing, setSyncing] = useState(false);
  
  // Reminder settings
  const [showReminderSettings, setShowReminderSettings] = useState(false);
  const [reminderSettings, setReminderSettings] = useState({
    enabled: true,
    timing: '24h',
    email_reminders: true,
    push_reminders: false
  });
  const [savingReminders, setSavingReminders] = useState(false);
  const [sendingTestReminder, setSendingTestReminder] = useState(false);

  const eventTypeConfig = {
    custom: { icon: CalendarBlank, label: 'Custom Event', color: '#2B4C6F' },
    meeting: { icon: Users, label: 'Meeting', color: '#8B5CF6' },
    site_visit: { icon: MapPin, label: 'Site Visit', color: '#10B981' },
    reminder: { icon: Bell, label: 'Reminder', color: '#F59E0B' },
    job_deadline: { icon: Warning, label: 'Job Deadline', color: '#EF4444' },
    milestone: { icon: CheckSquare, label: 'Milestone', color: '#10B981' },
    payment: { icon: CurrencyDollar, label: 'Payment', color: '#22C55E' },
    task: { icon: FileText, label: 'Task', color: '#6366F1' },
    project: { icon: Briefcase, label: 'Project', color: '#F97316' },
  };

  // Fetch events
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const start = startOfMonth(currentDate);
      const end = endOfMonth(currentDate);
      const response = await calendarAPI.getEvents(
        start.toISOString(),
        end.toISOString()
      );
      if (response.data.success) {
        setEvents(response.data.events || []);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  // Fetch reminder settings
  const fetchReminderSettings = useCallback(async () => {
    try {
      const response = await calendarAPI.getReminderSettings();
      if (response.data.success) {
        setReminderSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching reminder settings:', error);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    setUserType(localStorage.getItem('userType') || 'contractor');
    fetchEvents();
    fetchReminderSettings();
  }, [fetchEvents, fetchReminderSettings, navigate]);

  const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const handleToday = () => setCurrentDate(new Date());

  const handleCreateEvent = async () => {
    if (!newEvent.title.trim()) {
      toast.error('Please enter an event title');
      return;
    }

    try {
      const response = await calendarAPI.createEvent({
        ...newEvent,
        start_date: newEvent.start_date || new Date().toISOString(),
        end_date: newEvent.end_date || newEvent.start_date || new Date().toISOString(),
      });
      
      if (response.data.success) {
        toast.success('Event created successfully');
        setShowCreateModal(false);
        setNewEvent({
          title: '',
          description: '',
          start_date: '',
          end_date: '',
          all_day: true,
          event_type: 'custom',
          location: '',
          color: '#2B4C6F'
        });
        fetchEvents();
      }
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await calendarAPI.deleteEvent(eventId);
      toast.success('Event deleted');
      setShowEventModal(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const handleSyncAll = async () => {
    try {
      setSyncing(true);
      const response = await calendarAPI.syncAll();
      if (response.data.success) {
        toast.success(response.data.message || 'Calendar synced successfully');
        fetchEvents();
      } else {
        toast.error('Failed to sync calendar');
      }
    } catch (error) {
      console.error('Error syncing calendar:', error);
      toast.error('Failed to sync jobs and escrows');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveReminderSettings = async () => {
    try {
      setSavingReminders(true);
      const response = await calendarAPI.updateReminderSettings(reminderSettings);
      if (response.data.success) {
        toast.success('Reminder settings saved');
        setShowReminderSettings(false);
      }
    } catch (error) {
      console.error('Error saving reminder settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSavingReminders(false);
    }
  };

  const handleSendTestReminder = async () => {
    try {
      setSendingTestReminder(true);
      const response = await calendarAPI.sendTestReminder();
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message || 'Failed to send test reminder');
      }
    } catch (error) {
      console.error('Error sending test reminder:', error);
      toast.error('Failed to send test reminder');
    } finally {
      setSendingTestReminder(false);
    }
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      // Handle both start/end and start_date/end_date formats
      const startStr = event.start || event.start_date;
      const endStr = event.end || event.end_date || startStr;
      if (!startStr) return false;
      
      const eventStart = parseISO(startStr);
      const eventEnd = parseISO(endStr);
      return date >= eventStart && date <= eventEnd || isSameDay(date, eventStart);
    });
  };

  const renderCalendarDays = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    
    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const currentDay = day;
        const dayEvents = getEventsForDate(currentDay);
        const isCurrentMonth = isSameMonth(day, monthStart);
        const isSelectedDay = selectedDate && isSameDay(day, selectedDate);
        const isTodayDate = isToday(day);
        
        days.push(
          <div
            key={day.toString()}
            data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
            onClick={() => {
              setSelectedDate(currentDay);
              if (dayEvents.length === 1) {
                setSelectedEvent(dayEvents[0]);
                setShowEventModal(true);
              }
            }}
            className={`min-h-[100px] p-2 border-r-2 border-b-2 border-neutral-200 dark:border-neutral-700 cursor-pointer transition-colors
              ${!isCurrentMonth ? 'bg-neutral-100 dark:bg-neutral-800/50' : 'bg-white dark:bg-neutral-800'}
              ${isSelectedDay ? 'ring-2 ring-[#2B4C6F] ring-inset' : ''}
              ${isTodayDate ? 'bg-[#2B4C6F]/10 dark:bg-[#2B4C6F]/20' : ''}
              hover:bg-[#2B4C6F]/5 dark:hover:bg-[#2B4C6F]/10
            `}
          >
            <div className={`text-sm font-bold mb-1 ${
              isTodayDate ? 'text-[#2B4C6F]' : 
              !isCurrentMonth ? 'text-neutral-400 dark:text-neutral-500' : 
              'text-neutral-900 dark:text-white'
            }`}>
              {format(day, 'd')}
            </div>
            <div className="space-y-1">
              {dayEvents.slice(0, 3).map((event, idx) => {
                const eventType = event.eventType || event.event_type || 'custom';
                const config = eventTypeConfig[eventType] || eventTypeConfig.custom;
                return (
                  <div
                    key={event.id || event._id || idx}
                    data-testid={`calendar-event-${event.id || event._id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedEvent(event);
                      setShowEventModal(true);
                    }}
                    className="text-xs px-2 py-1 rounded font-bold truncate cursor-pointer border-l-4"
                    style={{ 
                      backgroundColor: `${event.color || config.color}20`,
                      borderLeftColor: event.color || config.color,
                      color: event.color || config.color
                    }}
                  >
                    {event.title}
                  </div>
                );
              })}
              {dayEvents.length > 3 && (
                <div className="text-xs text-[#2B4C6F] font-bold">
                  +{dayEvents.length - 3} more
                </div>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} className="grid grid-cols-7">
          {days}
        </div>
      );
      days = [];
    }

    return rows;
  };

  const renderUpcomingEvents = () => {
    const upcoming = events
      .filter(e => {
        const startStr = e.start || e.start_date;
        return startStr && new Date(startStr) >= new Date();
      })
      .sort((a, b) => new Date(a.start || a.start_date) - new Date(b.start || b.start_date))
      .slice(0, 5);

    if (upcoming.length === 0) {
      return (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">No upcoming events</p>
      );
    }

    return (
      <div className="space-y-3">
        {upcoming.map((event, idx) => {
          const eventType = event.eventType || event.event_type || 'custom';
          const config = eventTypeConfig[eventType] || eventTypeConfig.custom;
          const IconComponent = config.icon;
          const startStr = event.start || event.start_date;
          return (
            <div
              key={event.id || event._id || idx}
              onClick={() => {
                setSelectedEvent(event);
                setShowEventModal(true);
              }}
              className="flex items-start gap-3 p-3 rounded-lg border-2 border-neutral-200 dark:border-neutral-700 hover:border-[#2B4C6F] transition-colors cursor-pointer"
            >
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border-2"
                style={{ 
                  backgroundColor: `${event.color || config.color}20`,
                  borderColor: event.color || config.color
                }}
              >
                <IconComponent size={16} weight="bold" style={{ color: event.color || config.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-neutral-900 dark:text-white truncate">{event.title}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {startStr ? format(parseISO(startStr), 'MMM d, h:mm a') : 'No date'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderLegend = () => (
    <div className="flex flex-wrap gap-3">
      {Object.entries(eventTypeConfig).slice(0, 4).map(([key, config]) => (
        <div key={key} className="flex items-center gap-1.5 text-xs">
          <div 
            className="w-3 h-3 rounded"
            style={{ backgroundColor: config.color }}
          />
          <span className="text-neutral-600 dark:text-neutral-400 font-medium">{config.label}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <BreadcrumbsBox 
          customItems={[
            { label: 'Home', path: userType === 'worker' ? '/worker-dashboard' : '/contractor-dashboard', isHome: true },
            { label: 'Calendar', path: '/calendar', isCurrent: true }
          ]}
          className="mb-6"
        />
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-black text-neutral-900 dark:text-white flex items-center gap-3">
              <div className="w-12 h-12 bg-[#2B4C6F] rounded-xl flex items-center justify-center border-2 border-black dark:border-neutral-600 shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F]">
                <CalendarBlank size={24} weight="bold" className="text-white" />
              </div>
              Calendar
            </h1>
            <p className="mt-1 text-neutral-600 dark:text-neutral-400 font-medium">
              Track jobs, tasks, milestones, and payments
            </p>
          </div>
          <button
            onClick={() => {
              setNewEvent(prev => ({
                ...prev,
                start_date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
                end_date: format(new Date(), "yyyy-MM-dd'T'HH:mm")
              }));
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 h-12 px-6 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            data-testid="create-event-btn"
          >
            <Plus size={20} weight="bold" />
            Add Event
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Calendar */}
          <div className="lg:col-span-3 bg-white dark:bg-neutral-800 rounded-xl border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] overflow-hidden">
            {/* Calendar Controls */}
            <div className="p-4 border-b-2 border-neutral-200 dark:border-neutral-700 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <button 
                  onClick={handlePrevMonth}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-black dark:border-neutral-600 bg-white dark:bg-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-600 transition-colors"
                >
                  <CaretLeft size={20} weight="bold" className="text-neutral-900 dark:text-white" />
                </button>
                <button 
                  onClick={handleNextMonth}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-black dark:border-neutral-600 bg-white dark:bg-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-600 transition-colors"
                >
                  <CaretRight size={20} weight="bold" className="text-neutral-900 dark:text-white" />
                </button>
                <button 
                  onClick={handleToday}
                  className="px-4 h-10 rounded-lg border-2 border-black dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white font-bold hover:bg-neutral-100 dark:hover:bg-neutral-600 transition-colors"
                >
                  Today
                </button>
                <h2 className="text-xl font-black text-neutral-900 dark:text-white ml-4">
                  {format(currentDate, 'MMMM yyyy')}
                </h2>
              </div>
              {renderLegend()}
            </div>

            {/* Days of Week Header */}
            <div className="grid grid-cols-7 bg-neutral-100 dark:bg-neutral-700 border-b-2 border-neutral-200 dark:border-neutral-600">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="py-3 text-center text-sm font-bold text-neutral-600 dark:text-neutral-300">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            {loading ? (
              <div className="h-[600px] flex items-center justify-center">
                <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
              </div>
            ) : (
              <div>{renderCalendarDays()}</div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Upcoming Events */}
            <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] p-4">
              <h3 className="font-black text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
                <Clock size={20} weight="bold" className="text-[#2B4C6F]" />
                Upcoming Events
              </h3>
              {renderUpcomingEvents()}
            </div>

            {/* Quick Actions */}
            <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] p-4">
              <h3 className="font-black text-neutral-900 dark:text-white mb-4">Quick Add</h3>
              <div className="space-y-2">
                {[
                  { type: 'meeting', label: 'Meeting' },
                  { type: 'site_visit', label: 'Site Visit' },
                  { type: 'reminder', label: 'Reminder' },
                ].map(item => {
                  const config = eventTypeConfig[item.type];
                  const IconComponent = config.icon;
                  return (
                    <button
                      key={item.type}
                      onClick={() => {
                        setNewEvent(prev => ({
                          ...prev,
                          event_type: item.type,
                          color: config.color,
                          start_date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
                          end_date: format(new Date(), "yyyy-MM-dd'T'HH:mm")
                        }));
                        setShowCreateModal(true);
                      }}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-neutral-200 dark:border-neutral-700 hover:border-[#2B4C6F] transition-colors text-left"
                    >
                      <IconComponent size={20} weight="bold" style={{ color: config.color }} />
                      <span className="font-bold text-neutral-900 dark:text-white">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Sync Jobs & Escrows */}
            <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] p-4">
              <h3 className="font-black text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
                <ArrowsClockwise size={20} weight="bold" className="text-[#2B4C6F]" />
                Sync Data
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                Import job deadlines and escrow milestones from your active projects.
              </p>
              <button
                onClick={handleSyncAll}
                disabled={syncing}
                data-testid="sync-all-btn"
                className="w-full flex items-center justify-center gap-2 h-11 px-4 bg-[#2B4C6F] text-white font-bold rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {syncing ? (
                  <>
                    <SpinnerGap size={18} weight="bold" className="animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <ArrowsClockwise size={18} weight="bold" />
                    Sync All Jobs & Escrows
                  </>
                )}
              </button>
            </div>

            {/* Email Reminders */}
            <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] p-4">
              <h3 className="font-black text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
                <Bell size={20} weight="bold" className="text-[#2B4C6F]" />
                Email Reminders
              </h3>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-neutral-700 dark:text-neutral-300">
                  {reminderSettings.enabled ? 'Enabled' : 'Disabled'}
                </span>
                <button
                  onClick={() => setReminderSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    reminderSettings.enabled ? 'bg-[#2B4C6F]' : 'bg-neutral-300 dark:bg-neutral-600'
                  }`}
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    reminderSettings.enabled ? 'left-7' : 'left-1'
                  }`} />
                </button>
              </div>
              {reminderSettings.enabled && (
                <>
                  <div className="mb-3">
                    <label className="text-xs font-bold text-neutral-600 dark:text-neutral-400 mb-1 block">Remind me</label>
                    <select
                      value={reminderSettings.timing}
                      onChange={(e) => setReminderSettings(prev => ({ ...prev, timing: e.target.value }))}
                      className="w-full h-9 px-3 rounded-lg border-2 border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white text-sm font-medium"
                    >
                      <option value="24h">24 hours before</option>
                      <option value="12h">12 hours before</option>
                      <option value="6h">6 hours before</option>
                      <option value="1h">1 hour before</option>
                      <option value="30m">30 minutes before</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveReminderSettings}
                      disabled={savingReminders}
                      className="flex-1 flex items-center justify-center gap-2 h-9 px-3 bg-[#2B4C6F] text-white font-bold text-sm rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] hover:shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all disabled:opacity-50"
                    >
                      {savingReminders ? <SpinnerGap size={14} className="animate-spin" /> : <Gear size={14} weight="bold" />}
                      Save
                    </button>
                    <button
                      onClick={handleSendTestReminder}
                      disabled={sendingTestReminder}
                      className="flex-1 flex items-center justify-center gap-2 h-9 px-3 bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 font-bold text-sm rounded-lg border-2 border-neutral-300 dark:border-neutral-600 hover:border-[#2B4C6F] transition-all disabled:opacity-50"
                    >
                      {sendingTestReminder ? <SpinnerGap size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />}
                      Test
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Event Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[500px] border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">Create New Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="title" className="font-bold">Event Title *</Label>
              <Input
                id="title"
                value={newEvent.title}
                onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter event title"
                className="mt-1 border-2 border-black dark:border-neutral-600"
                data-testid="event-title-input"
              />
            </div>
            
            <div>
              <Label htmlFor="type" className="font-bold">Event Type</Label>
              <Select
                value={newEvent.event_type}
                onValueChange={(value) => {
                  const config = eventTypeConfig[value];
                  setNewEvent(prev => ({ 
                    ...prev, 
                    event_type: value,
                    color: config?.color || '#2B4C6F'
                  }));
                }}
              >
                <SelectTrigger className="mt-1 border-2 border-black dark:border-neutral-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(eventTypeConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded"
                          style={{ backgroundColor: config.color }}
                        />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start" className="font-bold">Start Date *</Label>
                <Input
                  id="start"
                  type="datetime-local"
                  value={newEvent.start_date}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, start_date: e.target.value }))}
                  className="mt-1 border-2 border-black dark:border-neutral-600"
                />
              </div>
              <div>
                <Label htmlFor="end" className="font-bold">End Date</Label>
                <Input
                  id="end"
                  type="datetime-local"
                  value={newEvent.end_date}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, end_date: e.target.value }))}
                  className="mt-1 border-2 border-black dark:border-neutral-600"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="location" className="font-bold">Location</Label>
              <Input
                id="location"
                value={newEvent.location}
                onChange={(e) => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Enter location (optional)"
                className="mt-1 border-2 border-black dark:border-neutral-600"
              />
            </div>

            <div>
              <Label htmlFor="description" className="font-bold">Description</Label>
              <Textarea
                id="description"
                value={newEvent.description}
                onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter description (optional)"
                className="mt-1 border-2 border-black dark:border-neutral-600"
                rows={3}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="all-day"
                checked={newEvent.all_day}
                onCheckedChange={(checked) => setNewEvent(prev => ({ ...prev, all_day: checked }))}
              />
              <Label htmlFor="all-day" className="font-bold">All Day Event</Label>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowCreateModal(false)}
              className="border-2 border-black dark:border-neutral-600 font-bold"
            >
              Cancel
            </Button>
            <button
              onClick={handleCreateEvent}
              className="h-10 px-6 bg-[#2B4C6F] text-white font-bold rounded-md border-2 border-black shadow-[2px_2px_0px_#000] hover:shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
              data-testid="save-event-btn"
            >
              Create Event
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Event Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="sm:max-w-[500px] border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
          {selectedEvent && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-black flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center border-2"
                    style={{ 
                      backgroundColor: `${selectedEvent.color || '#2B4C6F'}20`,
                      borderColor: selectedEvent.color || '#2B4C6F'
                    }}
                  >
                    {(() => {
                      const eventType = selectedEvent.eventType || selectedEvent.event_type || 'custom';
                      const config = eventTypeConfig[eventType] || eventTypeConfig.custom;
                      const IconComponent = config.icon;
                      return <IconComponent size={20} weight="bold" style={{ color: selectedEvent.color || config.color }} />;
                    })()}
                  </div>
                  {selectedEvent.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {(() => {
                  const startStr = selectedEvent.start || selectedEvent.start_date;
                  if (!startStr) return null;
                  return (
                    <div className="flex items-center gap-3 text-neutral-600 dark:text-neutral-400">
                      <Clock size={20} weight="bold" />
                      <span className="font-medium">
                        {format(parseISO(startStr), 'EEEE, MMMM d, yyyy')}
                        {!selectedEvent.allDay && !selectedEvent.all_day && ` at ${format(parseISO(startStr), 'h:mm a')}`}
                      </span>
                    </div>
                  );
                })()}
                
                {selectedEvent.location && (
                  <div className="flex items-center gap-3 text-neutral-600 dark:text-neutral-400">
                    <MapPin size={20} weight="bold" />
                    <span className="font-medium">{selectedEvent.location}</span>
                  </div>
                )}
                
                {selectedEvent.description && (
                  <div className="p-4 bg-neutral-100 dark:bg-neutral-700 rounded-lg">
                    <p className="text-neutral-700 dark:text-neutral-300">{selectedEvent.description}</p>
                  </div>
                )}
                
                {(selectedEvent.job_title || selectedEvent.jobTitle) && (
                  <div className="flex items-center gap-3 text-neutral-600 dark:text-neutral-400">
                    <Briefcase size={20} weight="bold" />
                    <span className="font-medium">Related to: {selectedEvent.job_title || selectedEvent.jobTitle}</span>
                  </div>
                )}
              </div>
              
              <div className="flex justify-between">
                <button
                  onClick={() => handleDeleteEvent(selectedEvent.id || selectedEvent._id)}
                  className="flex items-center gap-2 h-10 px-4 bg-red-500 text-white font-bold rounded-md border-2 border-black shadow-[2px_2px_0px_#000] hover:bg-red-600 transition-colors"
                  data-testid="delete-event-btn"
                >
                  <Trash size={16} weight="bold" />
                  Delete
                </button>
                <Button
                  variant="outline"
                  onClick={() => setShowEventModal(false)}
                  className="border-2 border-black dark:border-neutral-600 font-bold"
                >
                  Close
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Calendar;
